/**
 * GetSampleDataQuery - Query and Handler for fetching sample data
 * Follows MediatR pattern (Query + Handler in same file)
 */

import type { IQuery, IQueryHandler, IHttpClient } from '@/application/core';
import type { SampleDataResponse, SampleUser, SelectFilterOption } from './sampleData.types';

// ============================================================================
// Query (Request)
// ============================================================================

/**
 * Query to fetch all sample data
 * No parameters needed for this query
 */
export class GetSampleDataQuery implements IQuery<SampleDataResponse> {
  readonly type = 'GetSampleDataQuery' as const;
  // Parameters would go here if needed
  // constructor(public readonly userId?: string) {}
}

// ============================================================================
// Mock Data (to be replaced with real API calls)
// ============================================================================

const mockUsers: SampleUser[] = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john@example.com',
    status: 'active',
    role: 'admin',
    createdAt: '2024-01-15',
  },
  {
    id: '2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    status: 'active',
    role: 'user',
    createdAt: '2024-01-16',
  },
  {
    id: '3',
    name: 'Bob Johnson',
    email: 'bob@example.com',
    status: 'inactive',
    role: 'user',
    createdAt: '2024-01-17',
  },
  {
    id: '4',
    name: 'Alice Williams',
    email: 'alice@example.com',
    status: 'active',
    role: 'viewer',
    createdAt: '2024-01-18',
  },
  {
    id: '5',
    name: 'Charlie Brown',
    email: 'charlie@example.com',
    status: 'pending',
    role: 'user',
    createdAt: '2024-01-19',
  },
];

const mockMenuItems = [
  { id: 'dashboard', title: 'Dashboard', icon: 'Home' as any, path: '/', isActive: true },
  {
    id: 'mailbox',
    title: 'Mailboxes',
    icon: 'FileText' as any,
    path: '/mailbox',
    isActive: false,
    children: [
      { id: 'mailbox-list', title: 'All Mailboxes', path: '/mailbox/list' },
      { id: 'mailbox-create', title: 'Create Mailbox', path: '/mailbox/create' },
    ],
  },
  {
    id: 'trading-partners',
    title: 'Trading Partners',
    icon: 'Users' as any,
    path: '/trading-partners',
    isActive: false,
  },
  {
    id: 'documents',
    title: 'Documents',
    icon: 'FileText' as any,
    path: '/documents',
    isActive: false,
  },
  { id: 'reports', title: 'Reports', icon: 'BarChart' as any, path: '/reports', isActive: false },
  {
    id: 'settings',
    title: 'Settings',
    icon: 'Settings' as any,
    path: '/settings',
    isActive: false,
  },
];

const mockFooterLinks = [
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Support', href: '/support' },
  { label: 'Documentation', href: '/docs' },
];

const mockStatusOptions: SelectFilterOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
];

const mockRoleOptions: SelectFilterOption[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
  { value: 'viewer', label: 'Viewer' },
];

// ============================================================================
// Handler
// ============================================================================

/**
 * Handler for GetSampleDataQuery
 * Uses dependency injection for testability
 */
export class GetSampleDataQueryHandler implements IQueryHandler<
  GetSampleDataQuery,
  SampleDataResponse
> {
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(_: GetSampleDataQuery): Promise<SampleDataResponse> {
    // TODO: Replace with real API call when backend endpoint is ready
    // return this.httpClient.get<SampleDataResponse>('/api/sample-data');

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    return {
      users: mockUsers,
      menuItems: mockMenuItems,
      footerLinks: mockFooterLinks,
      statusOptions: mockStatusOptions,
      roleOptions: mockRoleOptions,
      version: '1.0.0',
      buildNumber: '2024.01.30',
    };
  }
}
